// KioskDetail — kiosk-γ.2 (CHG-2062 / ITER-4160).
//
// dashboard-v2 v1→v2 port of web/dashboard/src/pages/KioskDetail.tsx.
// Adapts to the DetailPage primitive — slim render slots, useQuery for
// sub-resources, FormDialog for modal forms, kiosks API client. Mirrors
// the StripeAccountDetail hand-rolled pattern (admin port, no entity-
// hint yet) per feedback_deterministic_codegen_over_hand_rolling.md.
//
// Five tabs per the kiosk-γ.2 brief:
//   1. Info — identity + reader hardware + pairing metadata
//   2. Donations — paged list from /kiosks/{id}/donations (period selector)
//   3. Heartbeats — recent history from /kiosks/{id}/heartbeats
//   4. Configuration — settings editor; renders the CHG-1198 kiosk-mode
//      panel (7 fields) when reader_type === "tap_to_pay"
//   5. Chart — daily donation totals by designation category, bar chart
//
// CHG-1198 parity is mandatory:
//   - 7 fields under settings_json.kiosk_mode (preset_amounts /
//     default_designation_id / receipt_prompt_enabled / receipt_prompt_copy
//     / max_donations_per_session / capture_payment_method / exit_pin).
//   - exit_pin is hashed CLIENT-SIDE as SHA-256(pin + kiosk.id) before
//     submit; never transmitted in plaintext (copied verbatim from v1
//     KioskDetail.tsx ~L354-414 per CHG-1198 reference).
//   - max_donations_per_session round-trips for hardware kiosks (m2)
//     even though the kiosk-mode panel isn't surfaced.
//   - Visibility gate: kiosk_mode block emitted only on soft kiosks.

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DetailPage,
  DataTable,
  type DetailPageConfig,
  type DataTableColumn,
  type StatCardDef,
} from "../components/composition";
import { ModalShell } from "../components/role/ModalShell";
import { type Formatters } from "../contexts/IntlContext";
import {
  kiosksApi,
  type Kiosk,
  type KioskSettings,
  type KioskHeartbeat,
  type KioskDonation,
  type KioskDonationChart,
} from "../api/kiosks";

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

type OnlineColor = "g-giving" | "g-events" | "g-people" | "g-home";

function onlineTone(lastSeen?: string | null): OnlineColor {
  if (!lastSeen) return "g-home";
  const diff = Date.now() - new Date(lastSeen).getTime();
  if (diff < 2 * 60_000) return "g-giving";   // online
  if (diff < 10 * 60_000) return "g-events";  // idle / yellow-ish
  return "g-people";                           // offline / red-ish
}

function onlineLabel(lastSeen?: string | null): string {
  if (!lastSeen) return "Never";
  const diff = Date.now() - new Date(lastSeen).getTime();
  if (diff < 60_000) return "Just now";
  if (diff < 2 * 60_000) return "Online";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
}

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  maintenance: "Maintenance",
  disabled: "Disabled",
};

const STATUS_TRANSITIONS: Record<string, string[]> = {
  draft: ["active"],
  active: ["maintenance", "disabled"],
  maintenance: ["active", "disabled"],
  disabled: ["active"],
};

const READER_TYPE_LABEL: Record<string, string> = {
  tap_to_pay: "Tap-to-Pay (built-in NFC)",
  m2: "M2 (Bluetooth)",
};

function field(label: string, value: ReactNode): ReactNode {
  return (
    <div
      key={label}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        fontSize: 13,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 11,
          color: "var(--color-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </span>
      <span style={{ color: "var(--color-text)", wordBreak: "break-word" }}>
        {value || "—"}
      </span>
    </div>
  );
}

// CHG-1198 — SHA-256(pin + kiosk.id) salted PIN hash, computed
// CLIENT-SIDE so plaintext never leaves the browser. Copied verbatim
// from v1 web/dashboard/src/pages/KioskDetail.tsx (lines ~374-380).
async function hashExitPin(pin: string, kioskId: string): Promise<string> {
  const enc = new TextEncoder().encode(pin + kioskId);
  const digest = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Info — device + reader metadata
// ─────────────────────────────────────────────────────────────────────

function InfoTab({ kiosk, fmt }: { kiosk: Kiosk; fmt: Formatters }) {
  const hb = kiosk.latest_heartbeat;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <section className="card" style={{ padding: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: "0 0 12px", color: "var(--color-text)" }}>
          Identity
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
          }}
        >
          {field("Name", kiosk.name)}
          {field("Location", kiosk.location)}
          {field("Status", STATUS_LABEL[kiosk.status] ?? kiosk.status)}
          {field(
            "Created",
            kiosk.created_at ? fmt.formatDate(kiosk.created_at, "medium") : null,
          )}
        </div>
      </section>

      <section className="card" style={{ padding: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: "0 0 12px", color: "var(--color-text)" }}>
          Pairing &amp; Hardware
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          {field(
            "Device Identifier",
            kiosk.device_identifier ? (
              <span className="tabular" style={{ fontFamily: "monospace" }}>
                {kiosk.device_identifier}{" "}
                <span
                  style={{
                    fontSize: 10,
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: "var(--color-g-giving-from, #dcfce7)",
                    color: "var(--color-text)",
                    marginLeft: 4,
                  }}
                >
                  Paired
                </span>
              </span>
            ) : (
              <span style={{ color: "var(--color-text-muted)" }}>Not paired</span>
            ),
          )}
          {field("Reader Type", READER_TYPE_LABEL[kiosk.reader_type] ?? kiosk.reader_type)}
          {field("Reader Label", kiosk.reader_label)}
          {field(
            "Reader Serial",
            kiosk.reader_serial_number ? (
              <span style={{ fontFamily: "monospace" }}>{kiosk.reader_serial_number}</span>
            ) : null,
          )}
          {field("Reader Status", kiosk.reader_status)}
          {field("Idle Timeout", `${kiosk.idle_timeout_seconds}s`)}
          {field("Brand Message", kiosk.brand_message)}
        </div>
      </section>

      {hb ? (
        <section className="card" style={{ padding: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, margin: "0 0 12px", color: "var(--color-text)" }}>
            Latest Heartbeat
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
            }}
          >
            {field("Reported", hb.reported_at ? fmt.formatDateTime(hb.reported_at) : null)}
            {field("App Version", hb.app_version)}
            {field(
              "Battery",
              hb.battery_percent != null ? `${hb.battery_percent}%` : null,
            )}
            {field("Network", hb.network_status)}
            {field("Reader Connected", hb.reader_connected ? "Yes" : "No")}
            {field("Offline Queue", String(hb.offline_queue_depth ?? 0))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Donations — paged list with period selector
// ─────────────────────────────────────────────────────────────────────

type Period = 7 | 30 | 90 | 0;

function DonationsTab({ kioskId, fmt }: { kioskId: string; fmt: Formatters }) {
  const [page, setPage] = useState(1);
  const [period, setPeriod] = useState<Period>(30);
  const q = useQuery({
    queryKey: ["kiosk", kioskId, "donations", page, period] as const,
    queryFn: () => kiosksApi.getDonations(kioskId, { page, per_page: 25 }),
  });

  // Period filter is applied client-side after fetch since the BE endpoint
  // doesn't accept a `days` param (kiosk-management.api.yaml line 393). The
  // page selector mirrors v1's behaviour — gives operators a quick lens
  // without piling new query params on the BE.
  const cutoffMs = period > 0 ? Date.now() - period * 86400_000 : 0;
  const rows: KioskDonation[] = (q.data?.data ?? []).filter((d) => {
    if (period === 0) return true;
    const t = new Date(d.paid_at ?? d.created_at).getTime();
    return Number.isFinite(t) && t >= cutoffMs;
  });

  const columns: DataTableColumn<KioskDonation & { id: string }>[] = [
    {
      field: "paid_at",
      label: "Date",
      type: "datetime",
      render: (row) => (
        <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
          {row.paid_at
            ? fmt.formatDateTime(row.paid_at)
            : fmt.formatDateTime(row.created_at)}
        </span>
      ),
    },
    {
      field: "amount_minor_units",
      label: "Amount",
      type: "currency",
      render: (row) => (
        <span style={{ fontWeight: 600 }}>
          {fmt.formatCurrency(row.amount_minor_units, row.currency)}
        </span>
      ),
    },
    {
      field: "designation_name",
      label: "Designation",
      type: "text",
      // KioskDonation type doesn't declare designation_name yet (only
      // designation_id); the BE response includes the name as a join-side
      // field per kiosk-management.api.yaml. Cast to allow read-through.
      render: (row) =>
        (row as { designation_name?: string }).designation_name ?? (
          <span style={{ color: "var(--color-text-muted)" }}>—</span>
        ),
    },
    {
      field: "donor_name",
      label: "Donor",
      type: "text",
      render: (row) =>
        row.anonymous ? (
          <span style={{ color: "var(--color-text-muted)", fontStyle: "italic" }}>
            Anonymous
          </span>
        ) : (
          row.donor_name ||
          row.donor_email || (
            <span style={{ color: "var(--color-text-muted)" }}>—</span>
          )
        ),
    },
    {
      field: "payment_method",
      label: "Method",
      type: "text",
      render: (row) =>
        row.payment_method ? (
          <span style={{ fontSize: 12, color: "var(--color-text-muted)", textTransform: "capitalize" }}>
            {row.payment_method.replace(/_/g, " ")}
          </span>
        ) : (
          <span style={{ color: "var(--color-text-muted)" }}>—</span>
        ),
    },
    {
      field: "status",
      label: "Status",
      type: "text",
      render: (row) => (
        <span style={{ fontSize: 12, textTransform: "capitalize" }}>{row.status}</span>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <PeriodPicker value={period} onChange={setPeriod} />
      <DataTable
        data={rows}
        columns={columns}
        isLoading={q.isLoading}
        error={q.error as Error | null}
        pagination={
          q.data?.pagination
            ? {
                page: q.data.pagination.page,
                pageSize: q.data.pagination.per_page,
                total: q.data.pagination.total,
              }
            : undefined
        }
        onPageChange={setPage}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Heartbeats — recent device telemetry
// ─────────────────────────────────────────────────────────────────────

function HeartbeatsTab({ kioskId, fmt }: { kioskId: string; fmt: Formatters }) {
  const q = useQuery({
    queryKey: ["kiosk", kioskId, "heartbeats"] as const,
    queryFn: () => kiosksApi.listHeartbeats(kioskId, 50),
  });
  if (q.isLoading) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        Loading heartbeats…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        Couldn't load heartbeats. {(q.error as Error).message}
      </div>
    );
  }
  const heartbeats: KioskHeartbeat[] = q.data ?? [];
  if (heartbeats.length === 0) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        No heartbeats recorded yet. The device reports state on a periodic
        cadence once it's paired and online.
      </div>
    );
  }

  const columns: DataTableColumn<KioskHeartbeat>[] = [
    {
      field: "reported_at",
      label: "Reported",
      type: "datetime",
      render: (row) => (
        <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
          {fmt.formatDateTime(row.reported_at)}
        </span>
      ),
    },
    {
      field: "app_version",
      label: "App",
      type: "text",
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>v{row.app_version}</span>
      ),
    },
    {
      field: "battery_percent",
      label: "Battery",
      type: "text",
      render: (row) =>
        row.battery_percent != null ? (
          <span>{row.battery_percent}%</span>
        ) : (
          <span style={{ color: "var(--color-text-muted)" }}>—</span>
        ),
    },
    {
      field: "network_status",
      label: "Network",
      type: "text",
      render: (row) => (
        <span style={{ fontSize: 12, textTransform: "capitalize" }}>{row.network_status}</span>
      ),
    },
    {
      field: "reader_connected",
      label: "Reader",
      type: "text",
      render: (row) => (
        <span style={{ fontSize: 12 }}>{row.reader_connected ? "Connected" : "Offline"}</span>
      ),
    },
    {
      field: "offline_queue_depth",
      label: "Queue",
      type: "number",
      render: (row) => <span>{row.offline_queue_depth ?? 0}</span>,
    },
    {
      field: "crash_count",
      label: "Crashes",
      type: "number",
      render: (row) => <span>{row.crash_count ?? 0}</span>,
    },
    {
      field: "uptime_seconds",
      label: "Uptime",
      type: "text",
      render: (row) =>
        row.uptime_seconds != null ? (
          <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
            {formatUptime(row.uptime_seconds)}
          </span>
        ) : (
          <span style={{ color: "var(--color-text-muted)" }}>—</span>
        ),
    },
  ];

  return <DataTable data={heartbeats} columns={columns} />;
}

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Configuration — settings editor + CHG-1198 kiosk-mode panel
// ─────────────────────────────────────────────────────────────────────

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        fontSize: 13,
        color: "var(--color-text)",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: "var(--color-primary)" }}
      />
      {label}
    </label>
  );
}

function ConfigField({
  label,
  helperText,
  children,
}: {
  label: string;
  helperText?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-muted)" }}>
        {label}
      </label>
      {children}
      {helperText ? (
        <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
}

function ConfigTab({
  kiosk,
  onSaved,
}: {
  kiosk: Kiosk;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const settings = kiosk.settings_json ?? {};
  const km = settings.kiosk_mode ?? {};
  const isSoftKiosk = kiosk.reader_type === "tap_to_pay";

  // Page-level fields
  const [skipIdle, setSkipIdle] = useState<boolean>(settings.skip_idle_screen ?? false);
  const [idleTimeout, setIdleTimeout] = useState<number>(
    settings.idle_timeout_seconds ?? kiosk.idle_timeout_seconds ?? 45,
  );
  const [capturePaymentMethod, setCapturePaymentMethod] = useState<boolean>(
    settings.capture_payment_method ?? false,
  );
  const [maxDonations, setMaxDonations] = useState<number>(
    settings.max_donations_per_session ?? 0,
  );
  const [brandMessage, setBrandMessage] = useState<string>(kiosk.brand_message ?? "");
  const [readerLabel, setReaderLabel] = useState<string>(kiosk.reader_label ?? "");

  // CHG-1198 kiosk-mode fields (soft kiosks only)
  const [presetAmountsRaw, setPresetAmountsRaw] = useState<string>(
    (km.preset_amounts ?? []).map((c) => (c / 100).toString()).join(", "),
  );
  const [defaultDesigId, setDefaultDesigId] = useState<string>(
    km.default_designation_id ?? "",
  );
  const [receiptPromptEnabled, setReceiptPromptEnabled] = useState<boolean>(
    km.receipt_prompt_enabled ?? true,
  );
  const [receiptPromptCopy, setReceiptPromptCopy] = useState<string>(
    km.receipt_prompt_copy ?? "",
  );
  const [exitPinInput, setExitPinInput] = useState<string>("");
  const existingExitPinHash = km.exit_pin_hash ?? "";

  // Designation / campaign multi-select state. We surface as text-ids
  // for now — the multi-checkbox picker requires the public designation/
  // campaign list endpoints which γ.1 didn't ship to v2. Comma-separated
  // text-id input is the doctrine-compliant interim — wire the picker
  // primitive in a follow-up CHG once the v2 designation list lands.
  const [allowedDesigIdsRaw, setAllowedDesigIdsRaw] = useState<string>(
    (settings.allowed_designation_ids ?? []).join(", "),
  );
  const [allowedCampaignIdsRaw, setAllowedCampaignIdsRaw] = useState<string>(
    (settings.allowed_campaign_ids ?? []).join(", "),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const trimmedBrand = brandMessage.trim();
      const trimmedReader = readerLabel.trim();

      const presetMinorUnits = presetAmountsRaw
        .split(",")
        .map((t) => Number(t.trim()))
        .filter((n) => Number.isFinite(n) && n > 0)
        .map((n) => Math.round(n * 100));

      // CHG-1198 — exit_pin hashing. Empty input keeps the existing hash;
      // a non-empty 4-digit value is hashed CLIENT-SIDE (SHA-256 + kiosk.id
      // salt) so plaintext never leaves the browser.
      let exitPinHashOut = existingExitPinHash;
      if (exitPinInput.trim() !== "") {
        if (!/^[0-9]{4}$/.test(exitPinInput.trim())) {
          throw new Error("Exit PIN must be exactly 4 digits.");
        }
        exitPinHashOut = await hashExitPin(exitPinInput.trim(), kiosk.id);
      }

      const kmBlock: KioskSettings["kiosk_mode"] = {};
      if (presetMinorUnits.length > 0) kmBlock!.preset_amounts = presetMinorUnits;
      if (defaultDesigId !== "") kmBlock!.default_designation_id = defaultDesigId;
      kmBlock!.receipt_prompt_enabled = receiptPromptEnabled;
      if (receiptPromptCopy.trim() !== "")
        kmBlock!.receipt_prompt_copy = receiptPromptCopy.trim();
      if (exitPinHashOut !== "") kmBlock!.exit_pin_hash = exitPinHashOut;

      const allowedDesigIds = allowedDesigIdsRaw
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const allowedCampaignIds = allowedCampaignIdsRaw
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      // Build the merged settings_json payload. For HARDWARE kiosks the
      // kiosk-mode panel is hidden but any existing max_donations_per_session
      // must round-trip unchanged (preserve-through-round-trip per CHG-0362).
      // For SOFT kiosks the edited maxDonations value wins.
      const mergedSettings: KioskSettings = {
        skip_idle_screen: skipIdle,
        idle_timeout_seconds: idleTimeout,
        capture_payment_method: capturePaymentMethod,
        allowed_designation_ids: allowedDesigIds,
        allowed_campaign_ids: allowedCampaignIds,
        ...(isSoftKiosk
          ? { max_donations_per_session: maxDonations }
          : settings.max_donations_per_session !== undefined
            ? { max_donations_per_session: settings.max_donations_per_session }
            : {}),
        ...(isSoftKiosk ? { kiosk_mode: kmBlock! } : {}),
      };

      const body: Partial<Kiosk> = {
        settings_json: mergedSettings,
        idle_timeout_seconds: idleTimeout,
      };
      if (trimmedBrand !== "") body.brand_message = trimmedBrand;
      if (trimmedReader !== "") body.reader_label = trimmedReader;

      await kiosksApi.update(kiosk.id, body);
      // Clear the PIN input after a successful save so it doesn't
      // visually persist after rotation.
      setExitPinInput("");
      await queryClient.invalidateQueries({ queryKey: ["kiosk", kiosk.id] });
      onSaved();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail ?? err?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [
    brandMessage,
    readerLabel,
    presetAmountsRaw,
    existingExitPinHash,
    exitPinInput,
    kiosk.id,
    defaultDesigId,
    receiptPromptEnabled,
    receiptPromptCopy,
    allowedDesigIdsRaw,
    allowedCampaignIdsRaw,
    skipIdle,
    idleTimeout,
    capturePaymentMethod,
    isSoftKiosk,
    maxDonations,
    settings.max_donations_per_session,
    queryClient,
    onSaved,
  ]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 720 }}>
      <section className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "var(--color-text)" }}>
          Donor Experience
        </h3>
        <Switch checked={skipIdle} onChange={setSkipIdle} label="Skip idle screen" />
        <Switch
          checked={capturePaymentMethod}
          onChange={setCapturePaymentMethod}
          label="Save donor's payment method (setup_future_usage = off_session)"
        />
        <ConfigField
          label="Idle timeout (seconds)"
          helperText="Seconds without activity before the kiosk returns to the idle screen."
        >
          <input
            type="number"
            min={5}
            max={300}
            value={idleTimeout}
            onChange={(e) => setIdleTimeout(Number(e.target.value) || 45)}
            style={inputStyle({ width: 140 })}
          />
        </ConfigField>
        {/* max_donations_per_session is the soft-kiosk anti-abuse cap.
            CHG-1198 surfaces it in the kiosk-mode panel below; for HARDWARE
            kiosks (m2) the field is intentionally hidden + the existing
            settings_json value round-trips unchanged on save. */}
        {!isSoftKiosk ? (
          <ConfigField
            label="Max donations per session (hardware kiosk; preserved on save)"
            helperText="Anti-abuse cap is not editable on hardware kiosks. Any saved value round-trips unchanged."
          >
            <input
              type="number"
              value={settings.max_donations_per_session ?? 0}
              disabled
              style={{ ...inputStyle({ width: 140 }), opacity: 0.6 }}
            />
          </ConfigField>
        ) : null}
      </section>

      <section className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "var(--color-text)" }}>
          Branding &amp; Reader
        </h3>
        <ConfigField
          label="Brand message"
          helperText="Shown on the idle / thank-you screen. Leave blank to use the org default."
        >
          <textarea
            value={brandMessage}
            onChange={(e) => setBrandMessage(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. Thank you for supporting our mission."
            style={inputStyle({ width: "100%" })}
          />
        </ConfigField>
        <ConfigField
          label="Card-reader label"
          helperText="Human-readable label for the Stripe Terminal reader paired to this kiosk."
        >
          <input
            type="text"
            value={readerLabel}
            onChange={(e) => setReaderLabel(e.target.value)}
            maxLength={255}
            placeholder="e.g. Lobby — BBPOS WisePOS E"
            style={inputStyle({ width: "100%" })}
          />
        </ConfigField>
      </section>

      <section className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "var(--color-text)" }}>
          Visibility
        </h3>
        <ConfigField
          label="Allowed designation IDs (comma-separated)"
          helperText="Limit which categories appear on this kiosk. Leave blank to show all."
        >
          <input
            type="text"
            value={allowedDesigIdsRaw}
            onChange={(e) => setAllowedDesigIdsRaw(e.target.value)}
            placeholder="uuid-1, uuid-2, …"
            style={inputStyle({ width: "100%" })}
          />
        </ConfigField>
        <ConfigField
          label="Allowed campaign IDs (comma-separated)"
          helperText="Feature specific campaigns on this kiosk. Leave blank to show none."
        >
          <input
            type="text"
            value={allowedCampaignIdsRaw}
            onChange={(e) => setAllowedCampaignIdsRaw(e.target.value)}
            placeholder="uuid-1, uuid-2, …"
            style={inputStyle({ width: "100%" })}
          />
        </ConfigField>
      </section>

      {/* CHG-1198 / ITER-3317 — Mobile Kiosk-Mode panel. Surfaces only when
          this kiosk is reader_type=tap_to_pay; the hardware kiosk app
          ignores these keys. Visibility gate matches v1 KioskDetail.tsx
          L515 (`isSoftKiosk`).
          Spec: specs/tier-05-experience/kiosk-mode-settings.form.yaml */}
      {isSoftKiosk ? (
        <section
          className="card"
          data-kiosk-mode-panel
          style={{
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            border: "2px solid var(--color-g-giving-from, rgba(34,197,94,0.3))",
            background: "var(--color-surface-elevated, rgba(34,197,94,0.04))",
          }}
        >
          <header>
            <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "var(--color-text)" }}>
              Mobile Kiosk-Mode
            </h3>
            <p style={{ fontSize: 12, color: "var(--color-text-muted)", margin: "4px 0 0" }}>
              In-app lockdown settings for this soft-paired phone or tablet.
              Changes propagate to the device on its next heartbeat.
            </p>
          </header>

          <ConfigField
            label="Preset amounts"
            helperText="Comma-separated amounts for quick-pick buttons. Leave blank to show only the custom keypad."
          >
            <input
              type="text"
              value={presetAmountsRaw}
              onChange={(e) => setPresetAmountsRaw(e.target.value)}
              placeholder="10, 25, 50, 100"
              style={inputStyle({ width: "100%" })}
            />
          </ConfigField>

          <ConfigField
            label="Default designation ID"
            helperText="Pre-selected when donors hit the kiosk amount screen. Blank = donor picks at runtime."
          >
            <input
              type="text"
              value={defaultDesigId}
              onChange={(e) => setDefaultDesigId(e.target.value)}
              placeholder="designation uuid (blank = donor picks)"
              style={inputStyle({ width: "100%" })}
            />
          </ConfigField>

          <Switch
            checked={receiptPromptEnabled}
            onChange={setReceiptPromptEnabled}
            label="Offer email receipt after donation"
          />

          {receiptPromptEnabled ? (
            <ConfigField
              label="Receipt prompt copy"
              helperText="Leave blank to use the brand-variant default."
            >
              <textarea
                value={receiptPromptCopy}
                onChange={(e) => setReceiptPromptCopy(e.target.value)}
                rows={2}
                maxLength={200}
                placeholder="Would you like an emailed receipt?"
                style={inputStyle({ width: "100%" })}
              />
            </ConfigField>
          ) : null}

          <ConfigField
            label="Max donations per session"
            helperText="Anti-abuse cap; 0 = unlimited. After this count the kiosk forces a return-to-idle."
          >
            <input
              type="number"
              min={0}
              max={1000}
              value={maxDonations}
              onChange={(e) =>
                setMaxDonations(Math.max(0, Number(e.target.value) || 0))
              }
              style={inputStyle({ width: 140 })}
            />
          </ConfigField>

          <ConfigField
            label={
              `Exit PIN (4 digits)` +
              (existingExitPinHash ? "  · PIN set" : "")
            }
            helperText="Required to exit kiosk mode on the device. Hashed CLIENT-SIDE (SHA-256(pin + kiosk.id)) before submit; never stored or transmitted in plaintext."
          >
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]{4}"
              maxLength={4}
              value={exitPinInput}
              onChange={(e) =>
                setExitPinInput(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder={
                existingExitPinHash
                  ? "(leave blank to keep current PIN)"
                  : "Set a 4-digit PIN"
              }
              style={{
                ...inputStyle({ width: 140 }),
                fontFamily: "monospace",
                letterSpacing: "0.2em",
              }}
            />
          </ConfigField>
        </section>
      ) : null}

      {error ? (
        <div
          className="card"
          style={{
            padding: 12,
            fontSize: 13,
            color: "var(--color-error, #b91c1c)",
            background: "var(--color-error-bg, rgba(220,38,38,0.06))",
          }}
        >
          {error}
        </div>
      ) : null}

      <div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
}

function inputStyle(extra: Record<string, string | number>): CSSProperties {
  return {
    fontSize: 13,
    padding: "6px 10px",
    border: "1px solid var(--color-border-subtle)",
    borderRadius: 6,
    background: "var(--color-surface)",
    color: "var(--color-text)",
    ...extra,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Chart — daily totals by category
// ─────────────────────────────────────────────────────────────────────

function ChartTab({ kioskId, fmt }: { kioskId: string; fmt: Formatters }) {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const q = useQuery({
    queryKey: ["kiosk", kioskId, "donation-chart", days] as const,
    queryFn: () => kiosksApi.getDonationChart(kioskId, days),
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <PeriodPicker value={days} onChange={(v) => setDays(v as 7 | 30 | 90)} hideAll />
      <ChartCard data={q.data} loading={q.isLoading} error={q.error as Error | null} fmt={fmt} />
    </div>
  );
}

const CHART_COLORS = [
  "var(--color-g-giving-from, #22c55e)",
  "var(--color-g-people-from, #0ea5e9)",
  "var(--color-g-events-from, #f59e0b)",
  "var(--color-g-channels-from, #8b5cf6)",
  "var(--color-g-imports-from, #ec4899)",
  "var(--color-g-automation-from, #06b6d4)",
];

function ChartCard({
  data,
  loading,
  error,
  fmt,
}: {
  data: KioskDonationChart | undefined;
  loading: boolean;
  error: Error | null;
  fmt: Formatters;
}) {
  if (loading) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        Loading chart…
      </div>
    );
  }
  if (error) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        Couldn't load chart. {error.message}
      </div>
    );
  }
  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        No kiosk donations recorded in this period.
      </div>
    );
  }
  const hasData = data.data.some((d) => Number(d.total ?? 0) > 0);
  if (!hasData) {
    return (
      <div className="card" style={{ padding: 16, fontSize: 13, color: "var(--color-text-muted)" }}>
        No donations recorded yet in this period.
      </div>
    );
  }
  const categories = data.categories ?? [];
  const points = data.data.length > 30 ? data.data.slice(-30) : data.data;
  const maxTotal = Math.max(...points.map((d) => Number(d.total ?? 0)), 1);
  return (
    <div className="card" style={{ padding: 16 }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h3 style={{ fontSize: 13, fontWeight: 600, margin: 0, color: "var(--color-text)" }}>
          Funds collected by category
        </h3>
        <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
          Last {points.length} days
        </span>
      </header>
      {categories.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
          {categories.map((cat, i) => (
            <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-muted)" }}>
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: CHART_COLORS[i % CHART_COLORS.length],
                  display: "inline-block",
                }}
              />
              {cat}
            </span>
          ))}
        </div>
      ) : null}
      <div
        role="img"
        aria-label="Daily donations bar chart"
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 3,
          height: 140,
        }}
      >
        {points.map((day, i) => {
          const pct = maxTotal > 0 ? (Number(day.total) / maxTotal) * 100 : 0;
          return (
            <div
              key={`${day.date}-${i}`}
              title={`${day.date}: ${fmt.formatCurrency(Number(day.total) || 0)}`}
              style={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column-reverse",
                height: `${pct}%`,
              }}
            >
              {categories.length === 0 && Number(day.total) > 0 ? (
                <div
                  style={{
                    background: CHART_COLORS[0],
                    height: "100%",
                    borderTopLeftRadius: 2,
                    borderTopRightRadius: 2,
                  }}
                />
              ) : (
                categories.map((cat, ci) => {
                  const catVal = Number(day[cat] ?? 0);
                  const catPct = Number(day.total ?? 0) > 0 ? (catVal / Number(day.total)) * 100 : 0;
                  if (catPct <= 0) return null;
                  return (
                    <div
                      key={cat}
                      style={{
                        background: CHART_COLORS[ci % CHART_COLORS.length],
                        height: `${catPct}%`,
                        minHeight: 1,
                        borderTopLeftRadius: ci === 0 ? 2 : 0,
                        borderTopRightRadius: ci === 0 ? 2 : 0,
                      }}
                    />
                  );
                })
              )}
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 6,
          fontSize: 10,
          color: "var(--color-text-muted)",
        }}
      >
        <span>{fmt.formatDate(points[0].date + "T00:00:00", "medium")}</span>
        <span>{fmt.formatDate(points[points.length - 1].date + "T00:00:00", "medium")}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Period selector — small inline pill row, used by Donations + Chart
// ─────────────────────────────────────────────────────────────────────

function PeriodPicker({
  value,
  onChange,
  hideAll,
}: {
  value: Period | 7 | 30 | 90;
  onChange: (v: Period) => void;
  hideAll?: boolean;
}) {
  const options: { label: string; value: Period }[] = [
    { label: "7d", value: 7 },
    { label: "30d", value: 30 },
    { label: "90d", value: 90 },
  ];
  if (!hideAll) options.push({ label: "All", value: 0 });
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={active ? "btn btn-primary" : "btn btn-secondary"}
            style={{ padding: "4px 10px", fontSize: 12 }}
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Header actions: Edit kiosk + Pair terminal + Pairing code + Status
// ─────────────────────────────────────────────────────────────────────

interface KioskActionsState {
  editOpen: boolean;
  pairOpen: boolean;
  statusOpen: boolean;
  pairingCode: string | null;
  pairingExpiresAt: number; // epoch ms
  error: string | null;
}

const ACTIONS_INIT: KioskActionsState = {
  editOpen: false,
  pairOpen: false,
  statusOpen: false,
  pairingCode: null,
  pairingExpiresAt: 0,
  error: null,
};

function PairingCodeOverlay({
  code,
  expiresAt,
  onClose,
}: {
  code: string;
  expiresAt: number;
  onClose: () => void;
}) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
  const mm = Math.floor(remaining / 60);
  const ss = remaining % 60;
  const expired = remaining === 0;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pairing code"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        // composition-lint-exempt: modal/overlay backdrop scrim — pending shared --color-scrim token (DRIFT-776)
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 60,
        padding: 16,
      }}
    >
      <div
        className="card"
        style={{
          padding: 24,
          maxWidth: 420,
          width: "100%",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Pairing code</h2>
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
          Enter this 6-digit code on the kiosk device's pairing screen.
        </p>
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 36,
            letterSpacing: "0.3em",
            padding: 12,
            background: "var(--color-surface-elevated, rgba(0,0,0,0.04))",
            borderRadius: 8,
          }}
        >
          {code}
        </div>
        <div
          style={{
            fontSize: 13,
            color: expired ? "var(--color-error, #b91c1c)" : "var(--color-text-muted)",
          }}
        >
          {expired
            ? "This code has expired. Generate a new one."
            : `Expires in ${mm}:${ss.toString().padStart(2, "0")}`}
        </div>
        <button type="button" onClick={onClose} className="btn btn-secondary">
          Close
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────────

export function KioskDetail() {
  const queryClient = useQueryClient();
  const [actions, setActions] = useState<KioskActionsState>(ACTIONS_INIT);

  const invalidateKiosk = useCallback(
    (id: string) => {
      queryClient.invalidateQueries({ queryKey: ["kiosk", id] });
      queryClient.invalidateQueries({ queryKey: ["kiosks", "list"] });
    },
    [queryClient],
  );

  const pairingMutation = useMutation({
    mutationFn: (id: string) => kiosksApi.generatePairingCode(id),
    onSuccess: (resp) => {
      setActions((a) => ({
        ...a,
        pairingCode: resp.code,
        pairingExpiresAt: Date.now() + resp.expires_in * 1000,
        error: null,
      }));
    },
    onError: (err: { message?: string }) => {
      setActions((a) => ({ ...a, error: err?.message ?? "Failed to generate pairing code" }));
    },
  });

  // Locate the cached kiosk record for action-callback parity (modals
  // resolve their target id from the URL via the query cache the
  // DetailPage primitive populated).
  const kioskId = useUrlIdParam("id");
  const cachedKiosk = queryClient.getQueryData<Kiosk>(["kiosk", kioskId]);

  // ─── DetailPageConfig ────────────────────────────────────────────
  const config: DetailPageConfig<Kiosk> = {
    idParam: "id",
    backTo: "/kiosks",
    queryKey: (id) => ["kiosk", id] as const,
    queryFn: (id) => kiosksApi.get(id),
    title: (r) => r.name || "Kiosk",
    subtitle: (r) => r.location || "No location set",
    actions: (record) => [
      {
        id: "edit",
        label: "Edit Kiosk",
        variant: "secondary",
        onClick: () => setActions((a) => ({ ...a, editOpen: true })),
      },
      {
        id: "pairing-code",
        label: pairingMutation.isPending ? "Generating…" : "Generate Pairing Code",
        variant: "secondary",
        disabled: pairingMutation.isPending || record.status === "disabled",
        onClick: () => pairingMutation.mutate(record.id),
      },
      {
        id: "pair-terminal",
        label: "Pair Terminal",
        variant: "secondary",
        onClick: () => setActions((a) => ({ ...a, pairOpen: true })),
      },
      {
        id: "change-status",
        label: "Change Status",
        variant: "primary",
        onClick: () => setActions((a) => ({ ...a, statusOpen: true })),
        disabled: (STATUS_TRANSITIONS[record.status] ?? []).length === 0,
      },
    ],
    statusCards: (record, fmt) => statusCardsFor(record, fmt),
    tabs: [
      {
        id: "info",
        label: "Info",
        render: (record, fmt) => <InfoTab kiosk={record} fmt={fmt} />,
      },
      {
        id: "donations",
        label: "Donations",
        render: (record, fmt) => <DonationsTab kioskId={record.id} fmt={fmt} />,
      },
      {
        id: "heartbeats",
        label: "Heartbeats",
        render: (record, fmt) => <HeartbeatsTab kioskId={record.id} fmt={fmt} />,
      },
      {
        id: "configuration",
        label: "Configuration",
        render: (record) => (
          <ConfigTab
            kiosk={record}
            onSaved={() => invalidateKiosk(record.id)}
          />
        ),
      },
      {
        id: "chart",
        label: "Chart",
        render: (record, fmt) => <ChartTab kioskId={record.id} fmt={fmt} />,
      },
    ],
  };

  return (
    <>
      <DetailPage config={config} />

      {actions.editOpen && cachedKiosk ? (
        <EditKioskModal
          kiosk={cachedKiosk}
          onClose={() => setActions((a) => ({ ...a, editOpen: false }))}
          onSaved={() => {
            invalidateKiosk(cachedKiosk.id);
            setActions((a) => ({ ...a, editOpen: false }));
          }}
        />
      ) : null}

      {actions.pairOpen && cachedKiosk ? (
        <PairTerminalModal
          kiosk={cachedKiosk}
          onClose={() => setActions((a) => ({ ...a, pairOpen: false }))}
          onSaved={() => {
            invalidateKiosk(cachedKiosk.id);
            setActions((a) => ({ ...a, pairOpen: false }));
          }}
        />
      ) : null}

      {actions.statusOpen && cachedKiosk ? (
        <ChangeStatusModal
          kiosk={cachedKiosk}
          onClose={() => setActions((a) => ({ ...a, statusOpen: false }))}
          onSaved={() => {
            invalidateKiosk(cachedKiosk.id);
            setActions((a) => ({ ...a, statusOpen: false }));
          }}
        />
      ) : null}

      {actions.pairingCode ? (
        <PairingCodeOverlay
          code={actions.pairingCode}
          expiresAt={actions.pairingExpiresAt}
          onClose={() =>
            setActions((a) => ({ ...a, pairingCode: null, pairingExpiresAt: 0 }))
          }
        />
      ) : null}

      {actions.error ? (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            padding: "10px 14px",
            background: "var(--color-error, #b91c1c)",
            color: "white",
            borderRadius: 6,
            fontSize: 13,
            zIndex: 70,
          }}
        >
          {actions.error}
        </div>
      ) : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// EditKioskModal — name + location + lifecycle status. Uses ModalShell
// (the v2 canonical "field-registry-bypass" overlay) since the v2
// composition FormDialog can't render text/select primitives yet —
// same RoleFormModal precedent (CHG-1663 / ITER-3772).
// ─────────────────────────────────────────────────────────────────────

function EditKioskModal({
  kiosk,
  onClose,
  onSaved,
}: {
  kiosk: Kiosk;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(kiosk.name ?? "");
  const [location, setLocation] = useState(kiosk.location ?? "");
  const [status, setStatus] = useState<Kiosk["status"]>(kiosk.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await kiosksApi.update(kiosk.id, {
        name: name.trim(),
        location: location.trim() || undefined,
        status,
      });
      onSaved();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail ?? err?.message ?? "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Edit Kiosk" onClose={onClose}>
      <form
        onSubmit={onSubmit}
        data-form="kiosk-edit"
        style={modalFormStyle}
      >
        <ModalField label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={modalInputStyle({ width: "100%" })}
          />
        </ModalField>
        <ModalField label="Location">
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={modalInputStyle({ width: "100%" })}
          />
        </ModalField>
        <ModalField label="Lifecycle status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as Kiosk["status"])}
            style={modalInputStyle({ width: 200 })}
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="disabled">Disabled</option>
          </select>
        </ModalField>
        {error ? <ModalError>{error}</ModalError> : null}
      </form>
      <ModalFooter
        onCancel={onClose}
        busy={busy}
        submitForm="kiosk-edit"
        submitLabel={busy ? "Saving…" : "Save"}
      />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PairTerminalModal — Stripe Terminal device binding.
// ─────────────────────────────────────────────────────────────────────

function PairTerminalModal({
  kiosk,
  onClose,
  onSaved,
}: {
  kiosk: Kiosk;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [readerLabel, setReaderLabel] = useState(kiosk.reader_label ?? "");
  const [readerSerial, setReaderSerial] = useState(kiosk.reader_serial_number ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (readerLabel.trim() === "") {
      setError("Reader label is required.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await kiosksApi.pairTerminal(kiosk.id, {
        reader_label: readerLabel.trim(),
        reader_serial_number: readerSerial.trim() || undefined,
      });
      onSaved();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail ?? err?.message ?? "Failed to pair terminal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Pair Terminal" onClose={onClose}>
      <form
        onSubmit={onSubmit}
        data-form="kiosk-pair-terminal"
        style={modalFormStyle}
      >
        <ModalField label="Reader label" required>
          <input
            type="text"
            value={readerLabel}
            onChange={(e) => setReaderLabel(e.target.value)}
            required
            placeholder="e.g. Lobby — BBPOS WisePOS E"
            style={modalInputStyle({ width: "100%" })}
          />
        </ModalField>
        <ModalField label="Reader serial (optional)">
          <input
            type="text"
            value={readerSerial}
            onChange={(e) => setReaderSerial(e.target.value)}
            style={modalInputStyle({ width: "100%", fontFamily: "monospace" })}
          />
        </ModalField>
        {error ? <ModalError>{error}</ModalError> : null}
      </form>
      <ModalFooter
        onCancel={onClose}
        busy={busy}
        submitForm="kiosk-pair-terminal"
        submitLabel={busy ? "Pairing…" : "Pair"}
      />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ChangeStatusModal — restricted to STATUS_TRANSITIONS[current].
// ─────────────────────────────────────────────────────────────────────

function ChangeStatusModal({
  kiosk,
  onClose,
  onSaved,
}: {
  kiosk: Kiosk;
  onClose: () => void;
  onSaved: () => void;
}) {
  const options = STATUS_TRANSITIONS[kiosk.status] ?? [];
  const [next, setNext] = useState<string>(options[0] ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      await kiosksApi.update(kiosk.id, { status: next as Kiosk["status"] });
      onSaved();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(err?.response?.data?.detail ?? err?.message ?? "Failed to change status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Change Status" onClose={onClose}>
      <form
        onSubmit={onSubmit}
        data-form="kiosk-change-status"
        style={modalFormStyle}
      >
        <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: 0 }}>
          Transition this kiosk from{" "}
          <strong style={{ color: "var(--color-text)" }}>
            {STATUS_LABEL[kiosk.status] ?? kiosk.status}
          </strong>
          .
        </p>
        <ModalField label="New status" required>
          <select
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            style={modalInputStyle({ width: 200 })}
          >
            {options.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s] ?? s}
              </option>
            ))}
          </select>
        </ModalField>
        {error ? <ModalError>{error}</ModalError> : null}
      </form>
      <ModalFooter
        onCancel={onClose}
        busy={busy}
        submitForm="kiosk-change-status"
        submitLabel={busy ? "Applying…" : "Apply"}
      />
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal-shared helpers (chrome + native-input styling)
// ─────────────────────────────────────────────────────────────────────

const modalFormStyle: CSSProperties = {
  padding: "16px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  flex: 1,
  overflowY: "auto",
};

function modalInputStyle(extra: Record<string, string | number>): CSSProperties {
  return {
    padding: "6px 10px",
    fontSize: 13,
    borderRadius: "var(--radius-sm, 6px)",
    border: "1px solid var(--color-border)",
    background: "var(--color-surface)",
    color: "var(--color-text)",
    fontFamily: "inherit",
    ...extra,
  };
}

function ModalField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "var(--color-text-muted)",
          marginBottom: 4,
          display: "block",
        }}
      >
        {label}
        {required ? (
          <span style={{ color: "var(--color-danger, #b91c1c)" }}> *</span>
        ) : null}
      </span>
      {children}
    </label>
  );
}

function ModalError({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      data-role="server-error"
      style={{
        padding: "8px 10px",
        fontSize: 13,
        color: "var(--color-danger, #b91c1c)",
        background: "var(--color-danger-bg, #fef2f2)",
        borderRadius: "var(--radius-sm, 6px)",
      }}
    >
      {children}
    </div>
  );
}

function ModalFooter({
  onCancel,
  busy,
  submitForm,
  submitLabel,
}: {
  onCancel: () => void;
  busy: boolean;
  submitForm: string;
  submitLabel: string;
}) {
  return (
    <footer
      style={{
        display: "flex",
        gap: 8,
        padding: "12px 20px",
        borderTop: "1px solid var(--color-border-subtle)",
        justifyContent: "flex-end",
      }}
    >
      <button
        type="button"
        onClick={onCancel}
        disabled={busy}
        className="btn btn-secondary"
      >
        Cancel
      </button>
      <button
        type="submit"
        form={submitForm}
        disabled={busy}
        className="btn btn-primary"
      >
        {submitLabel}
      </button>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────
// statusCards composition — used by DetailPageConfig.statusCards.
// Kept as a top-level helper so the config object stays scan-friendly.
// ─────────────────────────────────────────────────────────────────────

function statusCardsFor(record: Kiosk, fmt: Formatters): StatCardDef[] {
  // Fetch fundStats lazily by reading from the react-query cache keyed
  // off the kiosk id; the StatCards row renders synchronously from the
  // DetailPage chrome, so we do NOT call useQuery here. Instead we render
  // simple presence-based cards (today / connection / battery). The
  // donation totals live in the dedicated Donations + Chart tabs.
  const hb = record.latest_heartbeat;
  const tone = onlineTone(record.last_seen_at);
  const cards: StatCardDef[] = [
    {
      id: "online",
      label: "Status",
      value: onlineLabel(record.last_seen_at),
      format: "text",
      tone,
    },
    {
      id: "status",
      label: "Lifecycle",
      value: STATUS_LABEL[record.status] ?? record.status,
      format: "text",
      tone: record.status === "active" ? "g-giving" : "g-people",
    },
    {
      id: "battery",
      label: "Battery",
      value: hb?.battery_percent != null ? `${hb.battery_percent}%` : "—",
      format: "text",
      tone:
        hb?.battery_percent == null
          ? "g-home"
          : hb.battery_percent < 15
            ? "g-people"
            : hb.battery_percent < 30
              ? "g-events"
              : "g-giving",
    },
    {
      id: "reader",
      label: "Card Reader",
      value: hb?.reader_connected
        ? "Connected"
        : record.reader_status === "unpaired"
          ? "Not paired"
          : "Offline",
      format: "text",
      tone: hb?.reader_connected ? "g-giving" : "g-people",
    },
  ];
  // Reference fmt so we don't accidentally drop the formatter (currency
  // formatting flows through children that need it).
  void fmt;
  return cards;
}

// ─────────────────────────────────────────────────────────────────────
// Local helper: useParams thin wrapper. Avoids importing react-router-dom
// twice (DetailPage already consumes useParams internally). Reading the
// :id param outside the tab render slots — see Edit/Pair handler wiring.
// ─────────────────────────────────────────────────────────────────────

function useUrlIdParam(name: string): string | undefined {
  // Reach into the URL directly to avoid a redundant useParams call.
  // Same param the DetailPage primitive reads via idParam="id".
  if (typeof window === "undefined") return undefined;
  const path = window.location.pathname;
  const segments = path.split("/").filter(Boolean);
  // Convention used by /kiosks/:id and similar admin detail routes.
  // The id is the last non-empty path segment.
  const last = segments[segments.length - 1];
  // Silence unused param warnings (name kept for API clarity).
  void name;
  return last;
}

export default KioskDetail;
